import React, {useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import {
    Alert,
    Box,
    CircularProgress,
    Collapse,
    IconButton,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {useOktaAuth} from '@okta/okta-react';

const POLL_INTERVAL_MS = 30000;
const PAGE_SIZE = 10;

const CollapsibleRecordTable = ({ title, records, columns, color, headerBg }) => {
    const [open, setOpen] = useState(true);
    const [page, setPage] = useState(1);

    const pageCount = Math.ceil(records.length / PAGE_SIZE);
    const paginated = records.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    userSelect: "none",
                    marginBottom: "8px",
                }}
                onClick={() => setOpen((prev) => !prev)}
            >
                <Typography variant="h6" sx={{ color, flexGrow: 1 }}>
                    {title} ({records.length})
                </Typography>
                <IconButton size="small">
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={open}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: headerBg }}>
                            {columns.map((col) => (
                                <TableCell key={col.label}><strong>{col.label}</strong></TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginated.map((record, idx) => (
                            <TableRow key={idx}>
                                {columns.map((col) => (
                                    <TableCell key={col.label}>
                                        {col.render ? col.render(record) : record[col.field]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {pageCount > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", marginTop: "12px" }}>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={(_, val) => setPage(val)}
                            size="small"
                        />
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

export const ExcelUploadField = () => {
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const [jobData, setJobData] = useState(null);
    const [jobId, setJobId] = useState("");
    const [error, setError] = useState(null);

    const {authState} = useOktaAuth();

    const handleFileChange = (e) => {
        const selectedFile = e.target.files && e.target.files[0];
        setFile(selectedFile || null);
    };

    const handleUpload = async () => {
        try {
            if (!file) return;

            setProcessing(true);

            const params = new URLSearchParams({filename: file.name});

            let res = await fetch(`${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}area/resources/upload?${params.toString()}`, {
                method: 'POST',
                headers: {
                    'Authorization': authState.accessToken.accessToken
                }
            });

            if (!res.ok) {
                const errObj = await res.json().catch(() => ({}));
                throw new Error(errObj?.error?.message || "Failed to generate pre-signed S3 Url");
            }

            const {jobId, presignedUrl} = await res.json();
            setJobId(jobId);

            res = await fetch(presignedUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "x-amz-server-side-encryption": "aws:kms",
                    "x-amz-server-side-encryption-aws-kms-key-id": process.env.REACT_APP_QM_BUCKET_KEY_ID
                },
                body: file
            });

            if (!res.ok) {
                const errObj = await res.json().catch(() => ({}));
                throw new Error(errObj?.error?.message || "Failed to upload file using pre-signed url");
            }

            let completed = false;
            while (!completed) {
                await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

                try {
                    const statusRes = await fetch(
                        `${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}job?jobId=${jobId}`,
                        {
                            headers: {
                                Authorization: authState.accessToken.accessToken,
                                Accept: "application/json",
                            },
                        }
                    );

                    if (!statusRes.ok) {
                        console.error(`Poll request failed with status ${statusRes.status} — retrying`);
                        continue;
                    }

                    const data = await statusRes.json();
                    setJobData(data);

                    if (data.completed) completed = true;
                } catch (pollErr) {
                    console.error("Poll attempt failed — retrying:", pollErr);
                }
            }

            setProcessing(false);
            setSucceeded(true);
        } catch (err) {
            setProcessing(false);
            setError(err);
        }
    };

    const renderRecordTables = (data) => {
        if (!data) return null;

        const completedColumns = [
            { label: "Resource ID", field: "resourceId" },
            { label: "Entity Type", field: "entityType" },
            { label: "Quota", field: "quota" },
            { label: "Status", field: "status" },
        ];

        const failedColumns = [
            { label: "Resource ID", field: "resourceId" },
            { label: "Entity Type", field: "entityType" },
            { label: "Quota", field: "quota" },
            { label: "Status", field: "status" },
            { label: "Error", render: (r) => r.exception ?? "Unknown error" },
        ];

        return (
            <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "30px"}}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell><strong>Job ID</strong></TableCell>
                            <TableCell>{data.jobId}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><strong>Total Records</strong></TableCell>
                            <TableCell>{data.recordCount}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><strong>Completed</strong></TableCell>
                            <TableCell>{data.completedRecords?.length ?? 0}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><strong>Failed</strong></TableCell>
                            <TableCell>{data.failedRecords?.length ?? 0}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell>{data.completed ? "COMPLETE" : "IN PROGRESS"}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                {data.completedRecords?.length > 0 && (
                    <CollapsibleRecordTable
                        title="Completed Records"
                        records={data.completedRecords}
                        columns={completedColumns}
                        color="#2e7d32"
                        headerBg="#e8f5e9"
                    />
                )}

                {data.failedRecords?.length > 0 && (
                    <CollapsibleRecordTable
                        title="Failed Records"
                        records={data.failedRecords}
                        columns={failedColumns}
                        color="#c62828"
                        headerBg="#ffebee"
                    />
                )}
            </Box>
        );
    };

    const renderProcessing = () => (
        <Box sx={{display: "flex", flexDirection: "column", gap: "40px", alignItems: "center", width: "100%", paddingY: "50px"}}>
            {renderRecordTables(jobData)}
            <CircularProgress size={100} thickness={2} sx={{color: "#A60F2D"}}/>
        </Box>
    );

    const renderError = () => (
        <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", width: "100%", paddingY: "50px"}}>
            <Alert severity="error">{error.message}</Alert>
        </Box>
    );

    const renderSuccess = () => (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%", paddingY: "50px"}}>
            {renderRecordTables(jobData)}
        </Box>
    );

    if (processing) return renderProcessing();
    if (error) return renderError();
    if (succeeded) return renderSuccess();

    return (
        <Box sx={{marginTop: "20px", display: "flex", justifyContent: "center", alignItems: "center"}}>
            <Stack direction="row" spacing={2} alignItems="center" marginBottom={"30px"}>
                <TextField
                    label="Selected file"
                    value={file ? file.name : ''}
                    InputProps={{readOnly: true}}
                    width={"30%"}
                />
                <Button
                    variant="contained"
                    component="label"
                    style={{backgroundColor: "#A60F2D", color: "white"}}
                >
                    Choose File
                    <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileChange}/>
                </Button>
                <Button
                    variant="outlined"
                    disabled={!file}
                    sx={{color: "#A60F2D", borderColor: "#A60F2D"}}
                    onClick={handleUpload}
                >
                    Upload
                </Button>
            </Stack>
        </Box>
    );
};
